import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {MinuteDurationFormat} from '@app/shared/formatting.pipes';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {StatusAbsentModel} from '@app/models/status-absent.model';
import {StatusModel} from '@app/models/status.model';
import {absenceDuration} from '@app/models/status.constants';

@Component({
  selector: 'gt-status-settings',
  templateUrl: './status-settings.component.html',
  styleUrls: ['./status-settings.component.scss']
})
export class StatusSettingsComponent implements OnInit {
  @Input() login: string;
  @Input() orgname: string;
  @Input() isActiveAbsence = false;
  @Output() backObservable = new EventEmitter<void>();

  currentState: StatusSettingsState = StatusSettingsState.MAIN_SETTINGS_PAGE;
  statusProtected: StatusAbsentModel;
  dateTimeInput: moment.Moment;

  @Input()
  set status(status: StatusModel) {
    if (!this.statusProtected) {
      this.statusProtected = status as StatusAbsentModel;
    }
  }

  get status(): StatusModel {
    return this.statusProtected;
  }

  State = StatusSettingsState;

  get actionId(): number {
    return this.isActiveAbsence ? this.statusProtected.actionId : this.statusProtected.defaultActionId;
  }

  set actionId(actionId: number) {
    this.statusProtected.defaultActionId = actionId;
    if (this.isActiveAbsence) {
      this.statusProtected.actionId = actionId;
    }
  }

  get readReason(): boolean {
    return this.isActiveAbsence ? this.statusProtected.readReason : this.statusProtected.defaultReadReason;
  }

  set readReason(readReason: boolean) {
    this.statusProtected.defaultReadReason = readReason;
    if (this.isActiveAbsence) {
      this.statusProtected.readReason = readReason;
    }
  }

  get readTime(): boolean {
    return this.isActiveAbsence ? this.statusProtected.readTime : this.statusProtected.defaultReadTime;
  }

  set readTime(readTime: boolean) {
    this.statusProtected.defaultReadTime = readTime;
    if (this.isActiveAbsence) {
      this.statusProtected.readTime = readTime;
    }
  }

  get tempLogoutAgent(): boolean {
    return this.isActiveAbsence ? this.statusProtected.tempLogoutAgent : this.statusProtected.defaultTempLogoutAgent;
  }

  set tempLogoutAgent(tempLogoutAgent: boolean) {
    this.statusProtected.defaultTempLogoutAgent = tempLogoutAgent;
    if (this.isActiveAbsence) {
      this.statusProtected.tempLogoutAgent = tempLogoutAgent;
    }
  }

  constructor(
    private authenticationService: GtAuthenticationService,
    private absenceService: GtAbsenceService,
    public notificationService: GtNotificationService,
    private minuteDurationFormat: MinuteDurationFormat,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.login = this.login ? this.login : this.authenticationService.login;
    this.orgname = this.orgname ? this.orgname : this.authenticationService.oname;
    this.absenceService
      .getAbsenceReasons(this.orgname, this.login)
      .subscribe(x => (this.statusProtected.defaultOrganization = x[0] ? x[0].organization : this.status.organization));
    // the absences here needs the owner organization of the current absence
  }

  sendDefaultsToBackend() {
    this.absenceService
      .setAbsenceReasonForUser(this.orgname, this.login, this.statusProtected.createAbsenceReason())
      .subscribe({error: this.error});
  }

  changeDuration() {
    this.statusProtected.endTime = this.dateTimeInput.toDate();
    this.absenceService
      .editAbsenceEntry(this.statusProtected.createAbsenceEntry(this.orgname, this.login, this.statusProtected.absenceId))
      .subscribe(() => {
        this.changeState(StatusSettingsState.MAIN_SETTINGS_PAGE);
      }, this.error);
  }

  private error = error => {
    this.notificationService.errorNotification.next(error);
  };

  changeState(state: StatusSettingsState) {
    if (state === StatusSettingsState.CHANGE_DURATION) {
      this.dateTimeInput = moment(this.statusProtected.endTime);
    }
    this.currentState = state;
  }

  onBack() {
    this.backObservable.emit();
  }

  getDurationToShow(): string {
    const absence = absenceDuration.find(e => e.key === this.statusProtected.defaultDuration);
    return absence ? this.translate.instant(absence.text) : this.minuteDurationFormat.transform(this.statusProtected.defaultDuration);
  }
}

export enum StatusSettingsState {
  MAIN_SETTINGS_PAGE,
  SELECT_DURATION,
  SELECT_FORWARDING,
  CONFIGURE_MY_SERVICE,
  CHANGE_DURATION
}
